import { Ban, Check, MessageSquareWarning, PartyPopperIcon } from 'lucide-preact';
import { useEffect, useState, useCallback } from 'preact/hooks';

import { useLoyaltyProgramContext } from '@/context/LoyaltyProgramContext';
import { usePlatformHandlers } from '@/context/PlatformContext';
import { useWidgetConfig } from '@/context/WidgetConfigContext';
import { COLORS } from '@/helpers/constants/theme';
import { useHoverBackground } from '@/hooks/useHoverBackground';
import { E_MaximumUsageType } from '@/types/RegisteredCustomer';
import type { T_RedemptionsProps } from '@/types/RegisteredCustomer';

export const Redemptions = ({ redemptions, showRemainingUsages = false }: T_RedemptionsProps) => {
  if (!redemptions) return null;

  const { onDiscountRedemption, getActiveCart } = usePlatformHandlers();
  const {
    appliedCodes,
    inapplicableCodes,
    setAppliedCodes,
    setInapplicableCodes,
    collectionScopedCodes,
    setCollectionScopedCodes,
  } = useLoyaltyProgramContext();

  const { theme } = useWidgetConfig();

  const {
    backgroundColor: primaryButtonBackgroundColor = COLORS.BRAND_PRIMARY,
    textColor: primaryButtonTextColor = COLORS.TEXT_INVERSE,
  } = theme?.primaryButton ?? {};

  const {
    backgroundColor: primaryButtonHoverBackground,
    hoverHandlers: primaryButtonHoverHandlers,
  } = useHoverBackground({
    baseColor: primaryButtonBackgroundColor,
  });

  const [cartLength, setCartLength] = useState<number | undefined>(undefined);
  const [loadingCode, setLoadingCode] = useState<string | null>(null);

  const isEmptyCart = cartLength === 0;

  const updateCartState = useCallback(async () => {
    if (!getActiveCart) return;

    try {
      const cart = await getActiveCart();
      setCartLength(cart?.items?.length);

      const validCodes = cart?.discount_codes?.filter((codeObj) => codeObj.applicable) ?? [];
      const invalidCodes = cart?.discount_codes?.filter((codeObj) => !codeObj.applicable) ?? [];

      setAppliedCodes(validCodes.map((c) => c.code));
      setInapplicableCodes(invalidCodes.map((c) => c.code));
    } catch (err) {
      console.error('Failed to update cart state:', err);
    }
  }, [getActiveCart, setAppliedCodes, setInapplicableCodes]);

  useEffect(() => {
    void updateCartState();
  }, [updateCartState]);

  useEffect(() => {
    if (!getActiveCart) return;

    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      if (typeof args[0] === 'string' && /\/cart\/(change|add|update)/.test(args[0])) {
        void updateCartState();
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [getActiveCart, updateCartState]);

  useEffect(() => {
    const codes = redemptions
      .filter((redemption) => redemption.shopifyCollectionId)
      .map((redemption) => redemption.discountCode);
    if (codes.length) setCollectionScopedCodes((known) => [...new Set([...known, ...codes])]);
  }, [redemptions, setCollectionScopedCodes]);

  // Collection-limited codes Shopify reports as not applicable yet: kept on the cart
  // because they apply once an eligible product is added. Every other code behaves
  // as before.
  const pendingCollectionCodes = inapplicableCodes.filter((code) =>
    collectionScopedCodes.includes(code),
  );
  const otherInapplicableCodes = inapplicableCodes.filter(
    (code) => !collectionScopedCodes.includes(code),
  );

  const onRedeem = async (discountCode: string) => {
    try {
      setLoadingCode(discountCode);
      const cartCodes = [...appliedCodes, ...pendingCollectionCodes];
      const isAlreadyApplied = cartCodes.includes(discountCode);
      const updatedCodes = isAlreadyApplied
        ? cartCodes.filter((code) => code !== discountCode)
        : [...new Set([...cartCodes, discountCode])];

      if (onDiscountRedemption) {
        await onDiscountRedemption({
          code: updatedCodes.length > 0 ? updatedCodes.join(',') : '',
        });
      }

      await updateCartState();
    } catch (err) {
      console.error('Discount redemption failed:', err);
    } finally {
      setLoadingCode(null);
    }
  };

  return (
    <div className='space-y-2'>
      {redemptions?.length > 0 && appliedCodes?.length > 0 && (
        <div className='space-y-2'>
          {appliedCodes
            .filter((appliedCode) =>
              redemptions.some((redemption) => redemption.discountCode === appliedCode),
            )
            .map((appliedCode) => (
              <div
                key={appliedCode}
                className='flex items-center justify-around gap-2 rounded-md bg-green-100 px-3 py-2 text-sm text-green-700'
              >
                <PartyPopperIcon className='mt-[2px] h-8 w-8' />
                <div>
                  <h5 className='!text-green-600'>
                    <span className='font-bold! !text-green-600'>{appliedCode}</span> is
                    successfully applied
                  </h5>
                </div>
              </div>
            ))}
        </div>
      )}

      {cartLength !== undefined && isEmptyCart && redemptions?.length > 0 && (
        <div className='space-y-2'>
          <div className='flex items-center justify-around gap-2 rounded-md bg-yellow-100 px-3 py-2 text-sm text-yellow-700'>
            <MessageSquareWarning className='mt-[2px] h-8 w-8' />
            <h5 className='!text-yellow-500'>
              Add items to cart and the discount will be applied automatically
            </h5>
          </div>
        </div>
      )}

      {otherInapplicableCodes.length > 0 && !isEmptyCart && (
        <div className='space-y-2'>
          <div className='flex items-center justify-around gap-2 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700'>
            <Ban className='mt-[2px] h-8 w-8' />
            <h5 className='!text-gray-500'>
              The discount is not applicable (expired or no longer available)
            </h5>
          </div>
        </div>
      )}

      {pendingCollectionCodes.length > 0 && !isEmptyCart && (
        <div className='space-y-2'>
          <div className='flex items-center justify-around gap-2 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700'>
            <MessageSquareWarning className='mt-[2px] h-8 w-8' />
            <h5 className='!text-gray-500'>
              Some discounts only apply to selected products. They will apply once your cart has
              one.
            </h5>
          </div>
        </div>
      )}

      {redemptions?.map((benefit) => {
        const benefitCode = benefit.discountCode;
        const isApplied = appliedCodes.includes(benefitCode);
        const isInapplicable = inapplicableCodes.includes(benefitCode);
        // Stays clickable so the customer can remove it.
        const isPendingCollection = pendingCollectionCodes.includes(benefitCode);
        const isDisabled =
          ((isInapplicable || isEmptyCart) && !isPendingCollection) || loadingCode === benefitCode;

        return (
          <div
            key={benefit.redemptionId}
            className={`flex items-center justify-between rounded-lg bg-gray-50 p-3 transition-all duration-300 ${
              isApplied ? 'border border-green-400 bg-green-50' : ''
            }`}
          >
            <span className='text-sm font-medium'>
              {`${benefit.title} ${
                benefit.usageType === E_MaximumUsageType.LIMITED && showRemainingUsages
                  ? `(${benefit.remainingUses})`
                  : ''
              }`}
            </span>

            <button
              onClick={() => (!isInapplicable || isPendingCollection) && onRedeem(benefitCode)}
              disabled={isDisabled}
              {...primaryButtonHoverHandlers}
              title={
                isApplied
                  ? 'Click to remove this discount'
                  : isPendingCollection
                    ? 'Applies once your cart has an eligible product. Click to remove it'
                    : isInapplicable && !isEmptyCart
                      ? 'This code is not applicable currently'
                      : isDisabled
                        ? 'Add items to cart and the discount will be applied automatically'
                        : 'Click to apply this discount'
              }
              className={`flex items-center gap-1 rounded-full px-4 py-1 text-sm transition-colors ${
                isApplied
                  ? 'bg-green-600 text-white hover:bg-red-600'
                  : isDisabled
                    ? 'cursor-not-allowed'
                    : undefined
              } `}
              style={{
                backgroundColor:
                  isDisabled || isPendingCollection
                    ? COLORS.DISABLED_LIGHT
                    : primaryButtonHoverBackground,
                color:
                  isDisabled || isPendingCollection ? COLORS.DISABLED_DARK : primaryButtonTextColor,
              }}
            >
              {isApplied ? (
                <>
                  <Check className='h-4 w-4' /> Applied
                </>
              ) : isPendingCollection ? (
                'Not yet applicable'
              ) : isInapplicable && !isEmptyCart ? (
                'Inapplicable'
              ) : (
                'Redeem'
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
};
